import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ScrollView, View, Keyboard, StyleSheet } from 'react-native';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import InputFeedback from 'design-system/InputFeedback';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { delay } from 'utils/delay';
import useColors from 'hooks/useColors';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { capitalizeFirstCharacter, formatAmount } from 'utils/formatting';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { usePriceInputStyle } from '../hooks/usePriceInputStyle';
import { SellOrderType } from '../types';
import { useCurrentPrice, useStockMeta } from '../hooks/selectors';
import LowLimitPriceModal from '../components/LowLimitPriceModal';
const getValidationObject = (amountStr, orderType, currentPrice) => {
    if (!currentPrice) {
        return undefined;
    }
    const parsedAmount = Number(amountStr.replace(/[^0-9.]/g, ''));
    const amount = Number(parsedAmount);
    const changePercent = ((amount - currentPrice) / currentPrice) * 100;
    if (orderType === SellOrderType.LIMIT) {
        if (changePercent >= 100) {
            return {
                isError: true,
                title: 'Limit price is too high',
                description: 'The limit price is too high compared to the current market price.',
            };
        }
        if (changePercent <= -50) {
            return {
                isError: true,
                title: 'Limit price is too low',
                description: 'The limit price is too low compared to the current market price.',
            };
        }
    }
    else if (orderType === SellOrderType.STOP) {
        if (amount > currentPrice) {
            return {
                isError: true,
                title: 'Stop price is too high',
                description: 'The stop price cannot be higher than the current market price.',
            };
        }
        if (changePercent <= -50) {
            return {
                isError: true,
                title: 'Stop price is too low',
                description: 'The stop price is too low compared to the current market price.',
            };
        }
    }
    if (changePercent === 0 || (changePercent < 0.01 && changePercent > -0.01)) {
        return undefined;
    }
    return {
        isError: false,
        description: `Your ${orderType} price is ${Math.abs(changePercent).toFixed(2)}% ${changePercent > 0 ? 'higher' : 'lower'} than the current market price`,
    };
};
const SetLimitStopPrice = ({ route, navigation }) => {
    const { assetId, orderType, action } = route.params;
    const colors = useColors();
    const textInputRef = useRef(null);
    const currentPrice = useCurrentPrice(assetId);
    const stockMeta = useStockMeta(assetId);
    const symbol = stockMeta?.symbol;
    const [price, setPrice] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const priceInputStyle = usePriceInputStyle(isFocused);
    const [validationObject, setValidationObject] = useState(undefined);
    const handleGoBack = useCallback(() => {
        navigation.goBack();
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerLeft: () => (<View style={staticStyles.headerLeft}>
          <NavigationCloseButton onPress={handleGoBack}/>
        </View>),
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary, handleGoBack, staticStyles.headerLeft]);
    useAutoFocusInput(textInputRef);
    const onChangeText = useCallback((_formatted, extracted = '') => {
        setValidationObject(getValidationObject(extracted, orderType, currentPrice?.value));
        setPrice(extracted);
    }, [currentPrice?.value, orderType]);
    const focusInput = useCallback(() => {
        textInputRef.current?.focus();
    }, []);
    const handleNavigate = useCallback((parsedPrice) => {
        if (symbol) {
            navigation.pop();
            navigation.navigateDeprecated('InvestLimitStopSellInfoScreen', {
                symbol,
                assetId,
                action,
                orderType,
                amount: {
                    value: parsedPrice,
                    currency: 'USD',
                },
            });
        }
    }, [action, assetId, orderType, symbol]);
    const handleContinue = useCallback(() => {
        const validationObject = getValidationObject(price, orderType, currentPrice?.value);
        if (validationObject) {
            setValidationObject(validationObject);
            if (validationObject.isError) {
                return;
            }
        }
        Keyboard.dismiss();
        const parsedPrice = Number(price.replace(/[^0-9.]/g, ''));
        if (orderType === SellOrderType.LIMIT && currentPrice && parsedPrice < currentPrice.value) {
            LowLimitPriceModal.show(() => {
                delay(() => {
                    handleNavigate(parsedPrice);
                }, 150);
            }, focusInput);
            return;
        }
        handleNavigate(parsedPrice);
    }, [currentPrice, focusInput, handleNavigate, orderType, price]);
    return (<AppView type="secondary" avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={staticStyles.contentContainerStyles}>
        <ScreenInfoHeader smallSubTitle title={`Set ${orderType} ${action} price`} subTitle={symbol
            ? `Your order will execute if $${symbol} ${orderType === SellOrderType.LIMIT ? 'increases' : 'decreases'} to:`
            : ''}/>
        <CardView withPadding>
          <View style={staticStyles.priceRow}>
            <View style={staticStyles.flex}>
              <Text Text-16>{capitalizeFirstCharacter(orderType)} price</Text>
              {!!currentPrice && (<Text TextThin-14 Secondary>
                  Latest price: {formatAmount(currentPrice.value, currentPrice.currency, 2)}
                </Text>)}
            </View>
            <MoneyTextInput value={price} testID="input" ref={textInputRef} style={priceInputStyle} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} allowFontScaling={false} prefix="$" maximumIntegerDigits={9} placeholder="$0.00" onChangeText={onChangeText} underlineColorAndroid="transparent" selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder}/>
          </View>
        </CardView>
        {!!validationObject && (<InputFeedback type={validationObject.isError ? 'error' : 'info'} title={validationObject.title}>
            {validationObject.description}
          </InputFeedback>)}
      </ScrollView>
      <KeyboardAvoidingButton brand withFrame={false} title="Continue" onPress={handleContinue}/>
    </AppView>);
};
const staticStyles = StyleSheet.create({
    priceRow: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    contentContainerStyles: {
        flexGrow: 1,
    },
    validationObjectWrapper: {
        alignItems: 'flex-end',
    },
    headerLeft: {
        paddingTop: rem(8),
        marginLeft: -rem(4),
    },
});
export default SetLimitStopPrice;
